import { FunctionComponent, ReactNode } from 'react';
import { Box, SxProps } from '@mui/material';
import { useResponsive } from '../../contexts/ResponsiveContext';

type Variant = '50-50' | '60-40' | '40-60';

interface SideBySideSectionProps {
    left: ReactNode;
    right: ReactNode;
    /** 水平排列時比例，預設 50-50 */
    variant?: Variant;
    /** 水平排列時是否互換左右（僅在水平模式有效） */
    reverse?: boolean;
    /** 垂直對齊，預設 'start' */
    alignY?: 'start' | 'center' | 'end' | 'stretch';

    /** 是否改為垂直堆疊；不提供時，預設以 isScreenMd || isScreenSm 決定 */
    stacked?: boolean;
    /** 垂直堆疊時的顯示順序，預設 'left-first'；圖片在上時用 'right-first' */
    stackedOrder?: 'left-first' | 'right-first';

    /** 欄距（完整屬名），預設 '32px' */
    columnGap?: number | string;
    /** 列距（完整屬名），預設 '24px' */
    rowGap?: number | string;
    /** 在 isScreenLg 或 isScreenXl 時使用較小欄距（可選） */
    compactColumnGapOnLgXl?: number | string;

    containerSx?: SxProps;
    leftSx?: SxProps;
    rightSx?: SxProps;
    containerName?: string;
}

const SideBySideSection: FunctionComponent<SideBySideSectionProps> = ({
    left,
    right,
    variant = '50-50',
    reverse = false,
    alignY = 'start',
    stacked, // 可能為 undefined，稍後用預設規則
    stackedOrder = 'left-first',
    columnGap = '32px',
    rowGap = '24px',
    compactColumnGapOnLgXl,
    containerSx,
    leftSx,
    rightSx,
    containerName,
}) => {
    const { isMaxScreen, isScreenXxl, isScreenXl, isScreenLg, isScreenMd, isScreenSm, isScreenXs } =
        useResponsive();
    // 預設的垂直規則：在 MD 或 SM 時垂直
    const isStackedByDefault = isScreenMd || isScreenSm || isScreenXs;
    const isStacked = typeof stacked === 'boolean' ? stacked : isStackedByDefault;

    // 欄距：LG/XL 可用更小欄距
    const baseColumnGap = typeof columnGap === 'number' ? `${columnGap}px` : String(columnGap);
    const compactColumnGap =
        compactColumnGapOnLgXl != null
            ? typeof compactColumnGapOnLgXl === 'number'
                ? `${compactColumnGapOnLgXl}px`
                : String(compactColumnGapOnLgXl)
            : baseColumnGap;
    const columnGapValue = isScreenLg || isScreenXl ? compactColumnGap : baseColumnGap;

    const rowGapValue = typeof rowGap === 'number' ? `${rowGap}px` : String(rowGap);

    const desktopColumns =
        variant === '60-40' ? '3fr 2fr' : variant === '40-60' ? '2fr 3fr' : '1fr 1fr';

    return (
        <Box
            data-testid={containerName ?? 'side_by_side'}
            sx={{
                display: 'grid',
                gridTemplateColumns: isStacked ? '1fr' : desktopColumns,
                alignItems: alignY,
                columnGap: columnGapValue,
                rowGap: rowGapValue,
                ...containerSx,
            }}
        >
            <Box
                className="slot-left"
                sx={{
                    // 垂直時用 stackedOrder，水平時用 reverse
                    order: isStacked ? (stackedOrder === 'left-first' ? 1 : 2) : reverse ? 2 : 1,
                    ...leftSx,
                }}
            >
                {left}
            </Box>

            <Box
                className="slot-right"
                sx={{
                    order: isStacked ? (stackedOrder === 'left-first' ? 2 : 1) : reverse ? 1 : 2,
                    ...rightSx,
                }}
            >
                {right}
            </Box>
        </Box>
    );
};

export default SideBySideSection;

/** 建議用的圖片外框：你也可以直接自己傳 <Box component="img" .../> 進去 */
export const ImageBox: FunctionComponent<{ src: string; alt?: string; sx?: SxProps }> = ({
    src,
    alt,
    sx,
}) => (
    <Box
        sx={{
            width: '100%',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0px 6px 24px rgba(0,0,0,0.08)',
            ...sx,
        }}
    >
        <Box
            component="img"
            src={src}
            alt={alt ?? ''}
            sx={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }}
        />
    </Box>
);
